import * as React from "react";
import { useActiveRun } from "@/features/production/hooks";
import { useOee } from "@/features/oee/hooks";
import { LineCommandBar } from "@/features/production/LineCommandBar";
import { formatDisplay } from "@/lib/datetime-format";
import { formatRelativeTime } from "@/lib/display-labels";
import type { LineSummary } from "@/types/lines.types";
import type { ProductionRunSummary } from "@/types/production.types";
import type { OeeResponse } from "@opus-dt/shared-types";

const STAT_LABEL = "text-[10px] font-medium uppercase tracking-[0.06em] text-text-tertiary";
const STAT_VALUE = "font-mono text-[15px] font-semibold leading-none text-text-primary";

function deriveIndicators(
  run: ProductionRunSummary | null | undefined,
  oee: OeeResponse | undefined
): { throughput: number | null; refugo: number | null; eta: string } {
  if (!run) return { throughput: null, refugo: null, eta: "" };
  const hoursElapsed = (Date.now() - new Date(run.startedAt).getTime()) / 3_600_000;
  const throughput = hoursElapsed > 0 ? run.producedQty / hoursElapsed : null;
  const produced = oee?.raw?.producedQty ?? run.producedQty ?? 0;
  const refugo = oee && produced > 0 ? (oee.raw.totalLoss / produced) * 100 : null;
  const eta = run.expectedEnd ? formatDisplay(run.expectedEnd) : "";
  return { throughput, refugo, eta };
}

export function LineOrderPanel({ line }: { line: LineSummary }): React.ReactElement {
  const { data: run } = useActiveRun(line.id);
  const { data: oee } = useOee(line.id, "24h", { breakdown: true });

  const { throughput, refugo, eta } = deriveIndicators(run, oee);
  const elapsed = run && run.status === "ACTIVE" ? formatRelativeTime(run.startedAt) : null;

  return (
    <div className="space-y-3 rounded-[10px] border border-border-subtle bg-bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">
          Ordem em execução
        </p>
        {elapsed ? (
          <p className="text-[11px] text-text-tertiary">em produção há {elapsed}</p>
        ) : null}
      </div>

      {run ? (
        <dl className="grid grid-cols-3 gap-3 pt-1">
          <div className="space-y-1">
            <dt className={STAT_LABEL}>Throughput</dt>
            <dd className={STAT_VALUE}>
              {throughput == null ? "—" : `${throughput.toFixed(0)}`}
              {throughput != null ? (
                <span className="ml-1 text-[11px] font-medium text-text-tertiary">un/h</span>
              ) : null}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className={STAT_LABEL}>Fim planejado</dt>
            <dd className="text-[13px] font-semibold leading-none text-text-primary">
              {eta || "—"}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className={STAT_LABEL}>Refugo</dt>
            <dd className={STAT_VALUE}>{refugo == null ? "—" : `${refugo.toFixed(1)}%`}</dd>
          </div>
        </dl>
      ) : (
        <p className="text-[13px] text-text-tertiary">
          Sem ordem ativa. Inicie uma produção nas ações abaixo.
        </p>
      )}

      {}
      <LineCommandBar lineId={line.id} />
    </div>
  );
}
